import { Box, Checkbox, Divider, ListSubheader, MenuItem } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RootState } from '../../../redux/Store';
import FieldSVG from './FieldSVG/FieldSVG';
import { Trans, useTranslation } from 'react-i18next';
import { useWidgetHeader } from './WidgetHeaderContext';
import { DashboardWidgetContent } from '../dashboardTypes';
import { getStreamKeysForWidget } from '../../../utils/sensor-stream-tracker';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { ApplicationActions } from '../../../utils/constants';
import { persistDashboardLayoutToProject, updateWidgetInDashboardLayout } from '../persistDashboardWidgetContent';
import {
  formatFieldLabel,
  getFieldFamilyLabelDescriptor,
  getFieldOptionsForFamily,
  getFieldVariantLabel,
  getFieldVariantLabelDefault,
  getFieldVariantLabelDescriptor,
  getOrderedFieldFamilies,
  normalizeFieldSelection,
} from './FieldSVG/fields';
import { FIELD_FAMILY_LABEL_DEFAULT, type FieldSelection } from '../../../utils/fieldSelection';
import { useLiveStreamValue } from '../LiveGraphStore';

interface FieldWidgetProps {
  id: string;
  widgetFieldType?: unknown;
}

const createDefaultFieldWidgetContent = (fieldType: FieldSelection): DashboardWidgetContent => ({
  name: 'field',
  curve: 'linear',
  style: 'line',
  dataKey: 'all',
  sensorKeys: getStreamKeysForWidget('field', 'all'),
  options: {
    fieldType,
  },
  props: {},
});

const FieldWidget = ({ id, widgetFieldType }: FieldWidgetProps) => {
  const [fieldType, setFieldType] = useState<FieldSelection>(() => normalizeFieldSelection(widgetFieldType));
  const yaw = useLiveStreamValue('Locator Yaw', 0);
  const x = useLiveStreamValue('Position X', 0);
  const y = useLiveStreamValue('Position Y', 0);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const dashboardLayout = useAppSelector((state: RootState) => state.project.loadedProject?.dashboardLayout);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isHistoricMode = dashboardViewMode === ApplicationActions.DashboardViewMode.HISTORIC;
  const familyLabel = getFieldFamilyLabelDescriptor(fieldType.family);
  const variantLabel = getFieldVariantLabelDescriptor(fieldType);
  const selectedFieldLabel = formatFieldLabel(t(familyLabel.key, familyLabel.defaultValue), t(variantLabel.key, variantLabel.defaultValue));
  const orderedFamilies = getOrderedFieldFamilies();

  useEffect(() => {
    const normalizedFieldType = normalizeFieldSelection(widgetFieldType);

    setFieldType((currentFieldType) => {
      if (
        currentFieldType.family === normalizedFieldType.family &&
        currentFieldType.variant === normalizedFieldType.variant &&
        currentFieldType.accessoriesEnabled === normalizedFieldType.accessoriesEnabled
      ) {
        return currentFieldType;
      }

      return normalizedFieldType;
    });
  }, [widgetFieldType]);

  // When switching view modes, keep local field selection in sync with persisted widget props.
  // Otherwise, historic-only dropdown edits could "stick" when returning to live mode.
  useEffect(() => {
    setFieldType(normalizeFieldSelection(widgetFieldType));
  }, [dashboardViewMode, widgetFieldType]);

  const persistFieldSelection = useCallback(
    (nextFieldType: FieldSelection) => {
      if (isHistoricMode) return;
      if (!dashboardLayout) return;

      const mergeContent = (content: DashboardWidgetContent): DashboardWidgetContent => {
        const base = createDefaultFieldWidgetContent(nextFieldType);
        return {
          ...base,
          ...content,
          options: {
            ...base.options,
            ...content.options,
            fieldType: nextFieldType,
          },
        };
      };

      const nextLayout = updateWidgetInDashboardLayout(dashboardLayout, id, mergeContent);
      if (!nextLayout) return;

      persistDashboardLayoutToProject(dispatch, nextLayout);
    },
    [dashboardLayout, dispatch, id, isHistoricMode]
  );

  const handleFieldSelection = useCallback(
    (nextField: Pick<FieldSelection, 'family' | 'variant'>) => {
      setFieldType((prev) => {
        const nextSelection = { ...prev, ...nextField };
        persistFieldSelection(nextSelection);
        return nextSelection;
      });
    },
    [persistFieldSelection]
  );

  const handleAccessoriesVisibilitySelection = useCallback(
    (hidden: boolean) => {
      setFieldType((prev) => {
        const nextSelection = { ...prev, accessoriesEnabled: !hidden };
        persistFieldSelection(nextSelection);
        return nextSelection;
      });
    },
    [persistFieldSelection]
  );

  const fieldMenuItems = useMemo(
    () => (
      <>
        {orderedFamilies.flatMap((family, index) => [
          index > 0 ? <Divider key={`${family.value}-divider`} /> : null,
          <ListSubheader key={`${family.value}-header`} disableSticky>
            {t(family.label, FIELD_FAMILY_LABEL_DEFAULT[family.value])}
          </ListSubheader>,
          ...getFieldOptionsForFamily(family.value).map((option) => (
            <MenuItem
              key={`${option.family}-${option.variant}`}
              onClick={() => handleFieldSelection({ family: option.family, variant: option.variant })}
              selected={fieldType.family === option.family && fieldType.variant === option.variant}
            >
              {t(getFieldVariantLabel(option), getFieldVariantLabelDefault(option))}
            </MenuItem>
          )),
        ])}
        <Divider />
        <MenuItem onClick={() => handleAccessoriesVisibilitySelection(fieldType.accessoriesEnabled)}>
          <Checkbox
            checked={!fieldType.accessoriesEnabled}
            onChange={(_, checked) => handleAccessoriesVisibilitySelection(checked)}
            onClick={(event) => event.stopPropagation()}
          />
          <Trans i18nKey="fieldSelectionDialog.accessoriesToggle">Hide field objects</Trans>
        </MenuItem>
      </>
    ),
    [fieldType, handleAccessoriesVisibilitySelection, handleFieldSelection, orderedFamilies, t]
  );

  useWidgetHeader({ title: selectedFieldLabel, menuItems: fieldMenuItems }, [selectedFieldLabel, fieldMenuItems]);

  return (
    <Box flex="1" display="flex" overflow="hidden" justifyContent="center" alignItems="center" width="100%" height="100%">
      <FieldSVG x={x} y={y} yaw={yaw} fieldType={fieldType} />
    </Box>
  );
};

export default FieldWidget;
